import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Stethoscope, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { supabaseClient } from "../lib/supabaseClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    specialty: "",
    city: "",
    country: "",
    target_audience: "",
    language_preference: "English",
  });

  const specialties = [
    "Cardiology",
    "Dermatology",
    "Gynecology",
    "Orthopedics",
    "Pediatrics",
    "General Physician",
    "ENT",
    "Ophthalmology",
    "Psychiatry",
    "Dentistry",
  ];

  const countries = ["India", "UAE", "USA"];
  const languages = ["English", "Hinglish", "Hindi", "Arabic"];

  useEffect(() => {
    let isMounted = true;

    if (!supabaseClient) {
      navigate("/auth", { replace: true });
      return undefined;
    }

    supabaseClient.auth.getSession().then(({ data }) => {
      if (!isMounted) {
        return;
      }

      const sessionUser = data.session?.user ?? null;
      setCurrentUser(sessionUser);

      if (!sessionUser) {
        navigate("/auth", { replace: true });
        return;
      }

      // Route by role: admins to the admin panel, clients to record their avatar.
      (async () => {
        const token = data.session?.access_token;
        try {
          const adm = await axios.get(`${API}/admin/is-admin`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (adm.data?.is_admin) {
            navigate("/admin", { replace: true });
            return;
          }
        } catch (e) {
          // not an admin; continue as client
        }
        try {
          const res = await axios.get(
            `${API}/doctors/by-auth/${sessionUser.id}`,
          );
          const doctor = res.data;
          if (doctor && doctor.id) {
            navigate(`/video/${doctor.id}?tab=create`, { replace: true });
          }
        } catch (err) {
          // 404 means not found -> continue onboarding; other errors should log
          if (err.response && err.response.status !== 404) {
            console.error("Error checking doctor profile:", err);
          }
        }
      })();
    });

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step === 1 && (!formData.name || !formData.specialty)) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (step === 2 && (!formData.city || !formData.country)) {
      toast.error("Please fill in all required fields");
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      toast.error("Please sign in before creating a profile");
      navigate("/auth");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/doctors`, {
        ...formData,
        auth_user_id: currentUser.id,
        email: currentUser.email,
      });
      const doctorId = response.data.id;
      toast.success("Profile created successfully!");
      setTimeout(() => {
        navigate(`/dashboard/${doctorId}`);
      }, 1000);
    } catch (error) {
      console.error("Error creating profile:", error);
      toast.error("Failed to create profile. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <Stethoscope
            className="w-8 h-8 text-[#2C5F44] mr-2"
            strokeWidth={1.5}
          />
          <span
            className="text-2xl font-bold text-[#2C5F44]"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Doctor Content Engine
          </span>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <h1
              className="text-4xl font-bold text-[#1C2A21] mb-4"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Create Your Doctor Profile
            </h1>
            <p className="text-lg text-[#5C7063]">
              Help us personalize your content creation experience
            </p>
            {currentUser?.email && (
              <p className="mt-3 text-sm text-[#5C7063]">
                Signed in as {currentUser.email}
              </p>
            )}
          </div>

          {/* Progress Bar */}
          <div className="flex items-center justify-center gap-2 mb-12">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all ${
                  s === step
                    ? "w-12 bg-[#2C5F44]"
                    : s < step
                      ? "w-8 bg-[#82A08B]"
                      : "w-8 bg-[#82A08B]/20"
                }`}
              />
            ))}
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(44,95,68,0.06)] border border-[#2C5F44]/10">
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div>
                  <Label
                    htmlFor="name"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="onboarding-name-input"
                    placeholder="Dr. John Smith"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="specialty"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    Specialty *
                  </Label>
                  <Select
                    value={formData.specialty}
                    onValueChange={(val) => handleInputChange("specialty", val)}
                  >
                    <SelectTrigger
                      data-testid="onboarding-specialty-select"
                      className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                    >
                      <SelectValue placeholder="Select your specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map((spec) => (
                        <SelectItem key={spec} value={spec}>
                          {spec}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div>
                  <Label
                    htmlFor="city"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    City *
                  </Label>
                  <Input
                    id="city"
                    data-testid="onboarding-city-input"
                    placeholder="Mumbai"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="country"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    Country * (Important for compliance)
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(val) => handleInputChange("country", val)}
                  >
                    <SelectTrigger
                      data-testid="onboarding-country-select"
                      className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                    >
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div>
                  <Label
                    htmlFor="target_audience"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    Target Audience (Optional)
                  </Label>
                  <Input
                    id="target_audience"
                    data-testid="onboarding-audience-input"
                    placeholder="Young adults, Parents, Seniors, etc."
                    value={formData.target_audience}
                    onChange={(e) =>
                      handleInputChange("target_audience", e.target.value)
                    }
                    className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="language"
                    className="text-[#1C2A21] font-medium mb-2 block"
                  >
                    Language Preference
                  </Label>
                  <Select
                    value={formData.language_preference}
                    onValueChange={(val) =>
                      handleInputChange("language_preference", val)
                    }
                  >
                    <SelectTrigger
                      data-testid="onboarding-language-select"
                      className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
                    >
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang} value={lang}>
                          {lang}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <Button
                  onClick={() => setStep(step - 1)}
                  variant="outline"
                  className="rounded-full border-[#2C5F44]/20 text-[#2C5F44]"
                  data-testid="onboarding-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Back
                </Button>
              )}
              {step < 3 ? (
                <Button
                  onClick={handleNext}
                  className="button-pill ml-auto bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
                  data-testid="onboarding-next-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="button-pill ml-auto bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
                  data-testid="onboarding-submit-btn"
                >
                  {loading ? "Creating..." : "Complete Setup"}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
